import Checkbox from '@shared/components/Checkbox/Checkbox';
import Tooltip from '@shared/components/Tooltip/Tooltip';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';

import { ReactComponent as Help } from 'assets/icons/Help.svg';

export interface RolePermission {
  id: string;
  label: string;
  enabled: boolean;
}

interface PermissionCheckboxProps {
  permission: RolePermission;
  onChange?: () => void;
  disabled?: boolean;
  size?: 'sm' | 'md';
  className?: string;
  description?: string;
  isLockedByHigherLevel?: boolean;
}

const PermissionCheckbox = ({
  permission,
  onChange,
  disabled = false,
  size = 'sm',
  className,
  description,
  isLockedByHigherLevel = false,
}: PermissionCheckboxProps) => {
  const { t } = useTranslation();

  const isInteractive = !disabled && !isLockedByHigherLevel;
  const effectiveDisabled = disabled || isLockedByHigherLevel;
  const isChecked = isLockedByHigherLevel ? true : permission.enabled;

  const checkboxElement = isLockedByHigherLevel ? (
    <Tooltip label={t('workspace_roles.permission.locked_tooltip')} placement="top">
      <span className="inline-flex shrink-0">
        <Checkbox
          id={permission.id}
          checked={isChecked}
          onChange={isInteractive ? onChange : undefined}
          size={size}
          disabled={effectiveDisabled}
        />
      </span>
    </Tooltip>
  ) : (
    <Checkbox
      id={permission.id}
      checked={isChecked}
      onChange={isInteractive ? onChange : undefined}
      size={size}
      disabled={effectiveDisabled}
    />
  );

  return (
    <div className={cx('flex items-start space-x-2 min-w-0 w-full', className)}>
      {checkboxElement}
      <div
        className={cx(
          'inline-flex min-w-0 items-center gap-1 text-sm text-size-md font-light',
          !isInteractive && 'pointer-events-none'
        )}
      >
        <label htmlFor={permission.id} className={cx(isInteractive ? 'cursor-pointer' : 'pointer-events-none')}>
          {permission.label}
        </label>
        {description && (
          <Tooltip
            className="group inline-flex shrink-0 items-center pointer-events-auto"
            label={t(description)}
            placement="top"
          >
            <Help
              className="h-3.5 w-3.5 shrink-0 stroke-gray-50 stroke-[1.5] group-hover:stroke-gray-80"
              aria-label="permission description"
            />
          </Tooltip>
        )}
      </div>
    </div>
  );
};

export default PermissionCheckbox;
